import { useTranslation } from 'react-i18next'

import { EMPTY_OBJECT } from '../../../constants/common'
import { ActionSelect } from '../../Action'
import { BUTTON_HEIGHT_24 } from '../../Button'
import { BirdviewCard, BirdviewCardBody } from '../BirdviewCard'
import { QA_NAME_DASHBOARD_BIRDVIEW_CHART_ACTIONS } from '../DashboardBirdview.constants.qa'
import { checkIsBirdviewLoadingState } from '../DashboardBirdview.utils.sections'

import {
  useBirdviewChartCardActions,
  useBirdviewChartCardControls,
} from './BirdviewChartCard.hooks'
import {
  checkIsBirdviewChartControlsInChart,
  getBirdviewChartContent,
} from './BirdviewChartCard.utils'
import { BirdviewChartCardControls } from './BirdviewChartCardControls'
import { BirdviewChartCardSkeleton } from './BirdviewChartCardSkeleton'

import * as styles from './BirdviewChartCard.module.css'

// The controls sit outside the body to stay usable while the chart refreshes.
function BirdviewChartCard({
  chart,
  controls = EMPTY_OBJECT,
  dragHandleProps,
  rowSpan,
  section,
  title,
  onChangeControls,
  onCycleSize,
  onNavigate,
  onRemove,
}) {
  const { t } = useTranslation()

  const isLoading = checkIsBirdviewLoadingState(section.state)

  const {
    handleSelectMetric,
    handleSelectPeriod,
    metric,
    metricOptions,
    period,
    periodOptions,
  } = useBirdviewChartCardControls({
    chart,
    controls,
    rowSpan,
    onChangeControls,
  })
  const { actions, handleClickAction, handleOpen } =
    useBirdviewChartCardActions({
      chart,
      period,
      periodOptions,
      onChangeControls,
      onCycleSize,
      onNavigate,
      onRemove,
    })

  const controlsProps = {
    asOfNowKey: chart.asOfNowKey,
    metric,
    metricOptions,
    period,
    periodOptions,
    onSelectMetric: handleSelectMetric,
    onSelectPeriod: handleSelectPeriod,
  }

  return (
    <BirdviewCard
      dragHandleProps={dragHandleProps}
      headerMenu={
        <ActionSelect
          actions={actions}
          controlProps={{
            buttonProps: {
              height: BUTTON_HEIGHT_24,
              qaName: QA_NAME_DASHBOARD_BIRDVIEW_CHART_ACTIONS,
            },
          }}
          onClickAction={handleClickAction}
        />
      }
      isHeaderLoading={isLoading}
      title={title}
    >
      <div className={styles.body}>
        {checkIsBirdviewChartControlsInChart({
          chart,
          state: section.state,
        }) ? null : (
          <BirdviewChartCardControls {...controlsProps} isLoading={isLoading} />
        )}
        <BirdviewCardBody
          Skeleton={BirdviewChartCardSkeleton}
          emptyKey={chart.emptyKey ?? 'DashboardBirdview.ChartEmptyStateKey'}
          isRefreshing={section.isRefreshing}
          skeletonProps={{ type: chart.type }}
          state={section.state}
          onRetry={section.reload}
        >
          <div className={styles.chart}>
            {getBirdviewChartContent({
              chart,
              controlsProps,
              section,
              t,
              onOpen: handleOpen,
            })}
          </div>
        </BirdviewCardBody>
      </div>
    </BirdviewCard>
  )
}

export default BirdviewChartCard
